// Window-scoped drop target for audio files. Zero chrome when idle,
// a full-viewport target when something is being dragged. Every dropped
// audio/* file is piped through importAudioFile and reported via
// onStatus — the same StatusBar channel used elsewhere in the app.
//
// dragleave is tricky: it fires whenever the pointer crosses into a
// child element of the current dragover target. The counter pattern
// below (increment on enter, decrement on leave) gives us a clean
// "pointer is truly off the window" signal even with many children.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { StatusHandler } from '../../strudel';
import { getAudioContext, getSampleStorage } from '../../strudel';
import { importAudioFile, type ImportResult } from '../import';
import { describeResults } from './importReport';

type Props = {
  onStatus: StatusHandler;
};

export function DropOverlay({ onStatus }: Props) {
  const [active, setActive] = useState(false);
  const counterRef = useRef(0);

  const hasFiles = (e: DragEvent): boolean => {
    const types = e.dataTransfer?.types;
    if (!types) return false;
    // Chrome exposes a `contains('Files')`; Safari/Firefox use array
    // semantics. Handle both.
    if (typeof (types as unknown as DOMStringList).contains === 'function') {
      return (types as unknown as DOMStringList).contains('Files');
    }
    return Array.from(types as unknown as string[]).includes('Files');
  };

  const handleDragEnter = useCallback((e: DragEvent) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    counterRef.current += 1;
    if (counterRef.current === 1) setActive(true);
  }, []);

  const handleDragOver = useCallback((e: DragEvent) => {
    if (!hasFiles(e)) return;
    // preventDefault is mandatory here — without it, the browser's
    // default drop handler (navigate to the file URL) runs instead.
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    if (!hasFiles(e)) return;
    counterRef.current = Math.max(0, counterRef.current - 1);
    if (counterRef.current === 0) setActive(false);
  }, []);

  const handleDrop = useCallback(
    async (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      counterRef.current = 0;
      setActive(false);

      const files = e.dataTransfer?.files;
      if (!files || files.length === 0) return;

      const ctx = getAudioContext();
      const storage = getSampleStorage();
      if (!ctx || !storage) {
        onStatus(
          'Sample import unavailable — press Play once to boot the engine.',
          'warn',
        );
        return;
      }

      // Sequential decode (not Promise.all): Safari's decodeAudioData
      // has historically choked on many concurrent calls, and running
      // serially keeps the status-bar report ordering deterministic.
      const results: ImportResult[] = [];
      for (let i = 0; i < files.length; i++) {
        const f = files.item(i);
        if (!f) continue;
        results.push(await importAudioFile(f, storage, ctx));
      }
      const report = describeResults(results);
      if (report) onStatus(report.message, report.level);
    },
    [onStatus],
  );

  useEffect(() => {
    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('drop', handleDrop);
    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('drop', handleDrop);
    };
  }, [handleDragEnter, handleDragOver, handleDragLeave, handleDrop]);

  if (!active) return null;

  return (
    <div
      className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center"
      aria-hidden="true"
    >
      <div className="absolute inset-0 bg-zinc-950/80" />
      <div className="relative border-2 border-dashed border-yellow-300/80 rounded-xl px-8 py-6 text-yellow-100 bg-zinc-950/60 shadow-2xl">
        <div className="text-sm uppercase tracking-[0.3em] text-yellow-300/70 mb-1">
          Drop audio
        </div>
        <div className="text-lg">Adds to your local sample library</div>
        <div className="text-xs text-zinc-400 mt-2">
          wav / mp3 / ogg / opus / flac · up to 8 MB, 2 min
        </div>
      </div>
    </div>
  );
}

